import { loggingService } from './loggingService';

export interface ValidationRule {
  field: string;
  validate: (value: any, record?: any) => boolean | Promise<boolean>;
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: Array<{ field: string; message: string }>;
}

class ValidationService {
  private rules: Map<string, ValidationRule[]> = new Map();

  registerRule(tableName: string, rule: ValidationRule): void {
    const existingRules = this.rules.get(tableName) || [];
    existingRules.push(rule);
    this.rules.set(tableName, existingRules);
  }

  registerRules(tableName: string, rules: ValidationRule[]): void {
    const existingRules = this.rules.get(tableName) || [];
    this.rules.set(tableName, [...existingRules, ...rules]);
  }

  async validate(tableName: string, record: Record<string, any>): Promise<ValidationResult> {
    const rules = this.rules.get(tableName) || [];
    const errors: Array<{ field: string; message: string }> = [];

    for (const rule of rules) {
      try {
        const isValid = await rule.validate(record[rule.field], record);
        if (!isValid) {
          errors.push({
            field: rule.field,
            message: rule.message,
          });
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        loggingService.error('validation', 'Validation rule execution failed', error, { tableName, field: rule.field });
        errors.push({
          field: rule.field,
          message: 'Validation failed due to an error',
        });
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  async validateField(
    tableName: string,
    fieldName: string,
    value: any,
    record?: Record<string, any>
  ): Promise<{ valid: boolean; message?: string }> {
    const rules = this.rules.get(tableName) || [];
    const fieldRules = rules.filter((r) => r.field === fieldName);

    for (const rule of fieldRules) {
      try {
        const isValid = await rule.validate(value, record);
        if (!isValid) {
          return { valid: false, message: rule.message };
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        loggingService.error('validation', 'Field validation failed', error, { tableName, fieldName });
        return { valid: false, message: 'Validation failed due to an error' };
      }
    }

    return { valid: true };
  }

  clearRules(tableName?: string): void {
    if (tableName) {
      this.rules.delete(tableName);
    } else {
      this.rules.clear();
    }
  }
}

export const validationService = new ValidationService();

validationService.registerRules('projects', [
  {
    field: 'end_date',
    validate: (endDate, record) => {
      if (!endDate || !record?.start_date) return true;
      return new Date(endDate) >= new Date(record.start_date);
    },
    message: 'End date must be after or equal to start date',
  },
  {
    field: 'budget',
    validate: (budget) => {
      if (budget === null || budget === undefined) return true;
      return budget >= 0;
    },
    message: 'Budget must be a positive number',
  },
  {
    field: 'title',
    validate: (title) => {
      return title && title.trim().length > 0;
    },
    message: 'Project title is required',
  },
]);

validationService.registerRules('resources', [
  {
    field: 'availability',
    validate: (availability) => {
      if (availability === null || availability === undefined) return true;
      return availability >= 0 && availability <= 24;
    },
    message: 'Availability must be between 0 and 24 hours',
  },
  {
    field: 'title',
    validate: (title) => {
      return title && title.trim().length > 0;
    },
    message: 'Resource title is required',
  },
  {
    field: 'email',
    validate: (email) => {
      if (!email) return true;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    message: 'Invalid email format',
  },
]);

validationService.registerRules('resource_allocations', [
  {
    field: 'allocation_end',
    validate: (endDate, record) => {
      if (!endDate || !record?.allocation_start) return true;
      return new Date(endDate) >= new Date(record.allocation_start);
    },
    message: 'Allocation end date must be after or equal to start date',
  },
  {
    field: 'daily_hours',
    validate: (hours, record) => {
      if (hours === null || hours === undefined) return true;
      const interval = record?.allocation_interval || 'Daily';
      if (interval === 'Daily') return hours >= 0 && hours <= 24;
      if (interval === 'Weekly') return hours >= 0 && hours <= 168;
      if (interval === 'Monthly') return hours >= 0 && hours <= 744;
      return true;
    },
    message: 'Hours must be within valid range for the selected interval (Daily: 0-24h, Weekly: 0-168h, Monthly: 0-744h)',
  },
  {
    field: 'allocation_interval',
    validate: (interval, record) => {
      if (!interval || !record?.allocation_start || !record?.allocation_end) return true;
      const start = new Date(record.allocation_start);
      const end = new Date(record.allocation_end);
      const durationDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

      if (interval === 'Weekly' && durationDays < 7) {
        return false;
      }
      if (interval === 'Monthly' && durationDays < 28) {
        return false;
      }
      return true;
    },
    message: 'Allocation duration is too short for the selected interval (Weekly: min 7 days, Monthly: min 28 days)',
  },
  {
    field: 'cost_rate_at_allocation',
    validate: (rate) => {
      if (rate === null || rate === undefined) return true;
      return rate >= 0;
    },
    message: 'Cost rate must be a positive number',
  },
  {
    field: 'billing_rate_at_allocation',
    validate: (rate) => {
      if (rate === null || rate === undefined) return true;
      return rate >= 0;
    },
    message: 'Billing rate must be a positive number',
  },
]);

validationService.registerRules('customers', [
  {
    field: 'title',
    validate: (title) => {
      return title && title.trim().length > 0;
    },
    message: 'Customer title is required',
  },
]);

validationService.registerRules('partners', [
  {
    field: 'title',
    validate: (title) => {
      return title && title.trim().length > 0;
    },
    message: 'Partner title is required',
  },
  {
    field: 'email',
    validate: (email) => {
      if (!email) return true;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    message: 'Invalid email format',
  },
]);

validationService.registerRules('resource_role_types', [
  {
    field: 'title',
    validate: (title) => {
      return title && title.trim().length > 0;
    },
    message: 'Role title is required',
  },
  {
    field: 'cost_rate',
    validate: (rate) => {
      if (rate === null || rate === undefined) return true;
      return rate >= 0;
    },
    message: 'Cost rate must be a positive number',
  },
  {
    field: 'billing_rate',
    validate: (rate) => {
      if (rate === null || rate === undefined) return true;
      return rate >= 0;
    },
    message: 'Billing rate must be a positive number',
  },
]);

validationService.registerRules('departments', [
  {
    field: 'name',
    validate: (name) => {
      return name && name.trim().length > 0;
    },
    message: 'Department name is required',
  },
]);
